import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { unmapTeacherId } from '../utils/teacherIdMapper';
import { FaStar, FaMapMarkerAlt, FaClock, FaDollarSign, FaGraduationCap, FaFilter, FaSearch, FaUserGraduate, FaBook, FaHeart, FaEye } from 'react-icons/fa';

interface Teacher {
  _id: string;
  basicInfo: {
    fullName: string;
    country: string;
    profilePicture?: string;
    bio?: string;
  };
  teachingExperience: {
    yearsTeaching: number;
    education?: string;
  };
  rates: {
    hourlyRate: number;
    currency: string;
    offersFreeTrial: boolean;
  };
  stats: {
    averageRating: number;
    totalReviews: number;
    totalSessions: number;
  };
  profile: {
    bio: string;
    subjects: string[];
    languages: string[];
  };
  teachingOffer: {
    targetStudents: string[];
    teachingMethods: string[];
  };
  availability: {
    isAvailable: boolean;
    timezone?: string;
  };
}

const Teachers: React.FC = () => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState({
    country: '',
    minRate: '',
    maxRate: '',
    targetStudents: '',
    sort: 'rating',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [favorites, setFavorites] = useState<string[]>([]);

  useEffect(() => {
    fetchTeachers();
  }, [currentPage, filters]);

  useEffect(() => {
    // Load favorites from localStorage
    const savedFavorites = localStorage.getItem('teacher-favorites');
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, []);

  // Mock teacher data for demonstration
  const mockTeachers: Teacher[] = [
    {
      _id: '1',
      basicInfo: {
        fullName: 'Negin Nickparsa',
        country: 'USA',
        profilePicture: '/images/profiles/negin-nickparsa.jpg',
        bio: 'Native Persian speaker with 6 years of teaching experience, specializing in children and family education'
      },
      teachingExperience: {
        yearsTeaching: 6,
        education: 'Bachelor\'s in Education, University of Toronto'
      },
      rates: {
        hourlyRate: 28,
        currency: 'USD',
        offersFreeTrial: true
      },
      stats: {
        averageRating: 4.9,
        totalReviews: 98,
        totalSessions: 720
      },
      profile: {
        bio: 'I am passionate about teaching Persian to learners of all ages, with special expertise in children\'s education and family learning. My background in education and multilingual teaching allows me to create engaging, effective lessons that make Persian accessible to everyone.',
        subjects: ['Beginner Persian', 'Children\'s Persian', 'Family Persian', 'Persian for Young Learners', 'Cultural Activities', 'Persian Songs & Stories'],
        languages: ['Persian (Native)', 'English (Fluent)', 'French (Advanced)', 'Turkish (Intermediate)']
      },
      teachingOffer: {
        targetStudents: ['Beginner', 'Intermediate', 'Children', 'Families', 'Young learners', 'Seniors'],
        teachingMethods: ['Interactive games', 'Songs and stories', 'Cultural immersion', 'Family-focused learning']
      },
      availability: {
        isAvailable: true,
        timezone: 'America/Toronto'
      }
    },
    {
      _id: '2',
      basicInfo: {
        fullName: 'Salman Zahabioun',
        country: 'Iran',
        profilePicture: '/images/profiles/Salman Zahabioun.jpg',
        bio: 'Professional Persian instructor with expertise in business Persian and academic writing'
      },
      teachingExperience: {
        yearsTeaching: 5,
        education: 'PhD in Middle Eastern Studies, UCLA'
      },
      rates: {
        hourlyRate: 30,
        currency: 'USD',
        offersFreeTrial: true
      },
      stats: {
        averageRating: 4.8,
        totalReviews: 89,
        totalSessions: 320
      },
      profile: {
        bio: 'I bring a unique perspective to Persian language teaching, combining traditional methods with modern technology.',
        subjects: ['Modern Persian', 'Persian Poetry', 'Iranian History', 'Academic Persian'],
        languages: ['Persian', 'English', 'French']
      },
      teachingOffer: {
        targetStudents: ['Intermediate', 'Advanced', 'Academic students'],
        teachingMethods: ['Technology-enhanced', 'Literature-based', 'Cultural exchange']
      },
      availability: {
        isAvailable: true,
        timezone: 'America/Los_Angeles'
      }
    },
    {
      _id: '3',
      basicInfo: {
        fullName: 'Maryam Ghorbali',
        country: 'USA',
        profilePicture: '/images/profiles/maryam-ghorbali.jpg',
        bio: 'Dedicated Persian teacher with a focus on beginner-friendly instruction'
      },
      teachingExperience: {
        yearsTeaching: 6,
        education: 'Bachelor\'s in Education, University of Toronto'
      },
      rates: {
        hourlyRate: 30,
        currency: 'USD',
        offersFreeTrial: true
      },
      stats: {
        averageRating: 4.7,
        totalReviews: 156,
        totalSessions: 580
      },
      profile: {
        bio: 'I specialize in teaching Persian to beginners and children. My teaching style is patient, encouraging, and focused on building confidence.',
        subjects: ['Beginner Persian', 'Children\'s Persian', 'Basic Grammar', 'Cultural Activities'],
        languages: ['Persian', 'English', 'French']
      },
      teachingOffer: {
        targetStudents: ['Beginner', 'Children', 'Families', 'Young learners'],
        teachingMethods: ['Play-based learning', 'Family-friendly', 'Interactive games']
      },
      availability: {
        isAvailable: true,
        timezone: 'America/Toronto'
      }
    },

    {
      _id: '4',
      basicInfo: {
        fullName: 'Shera Malayeri',
        country: 'USA',
        bio: 'Enthusiastic Persian teacher passionate about making Persian accessible'
      },
      teachingExperience: {
        yearsTeaching: 4,
        education: 'Master\'s in Applied Linguistics, University of Melbourne'
      },
      rates: {
        hourlyRate: 28,
        currency: 'USD',
        offersFreeTrial: true
      },
      stats: {
        averageRating: 4.6,
        totalReviews: 78,
        totalSessions: 290
      },
      profile: {
        bio: 'I believe that learning Persian should be enjoyable and accessible. I use modern teaching methods and technology.',
        subjects: ['Conversational Persian', 'Persian for Travel', 'Modern Persian', 'Cultural Exchange'],
        languages: ['Persian', 'English', 'Spanish']
      },
      teachingOffer: {
        targetStudents: ['Beginner', 'Intermediate', 'Travelers', 'Language enthusiasts'],
        teachingMethods: ['Conversation-focused', 'Travel preparation', 'Cultural exchange']
      },
      availability: {
        isAvailable: true,
        timezone: 'Australia/Sydney'
      }
    },
    {
      _id: '5',
      basicInfo: {
        fullName: 'Bahar Erfanian',
        country: 'United States',
        bio: 'Experienced Persian instructor specializing in advanced literature and cultural studies'
      },
      teachingExperience: {
        yearsTeaching: 3,
        education: 'Master of Science (MS), Computer Science - California State University, Long Beach (2013-2015), Bachelor of Science (BS), Computer Software Engineering - Mazandaran University of Science and Technology (2008-2013)'
      },
      rates: {
        hourlyRate: 28,
        currency: 'USD',
        offersFreeTrial: true
      },
      stats: {
        averageRating: 4.8,
        totalReviews: 45,
        totalSessions: 180
      },
      profile: {
        bio: 'As a computer science professional with deep Persian cultural roots, I bring a unique perspective to Persian language teaching. My technical background helps me create structured, logical approaches to language learning while maintaining the beauty and richness of Persian culture.',
        subjects: ['Persian Grammar', 'Conversational Persian', 'Persian for Professionals', 'Iranian Culture', 'Persian Literature', 'Technical Persian'],
        languages: ['Persian (Native)', 'English (Fluent)', 'Technical English']
      },
      teachingOffer: {
        targetStudents: ['Beginner', 'Intermediate', 'Advanced', 'Professional learners', 'Heritage speakers'],
        teachingMethods: ['Structured learning', 'Technology-enhanced', 'Professional focus', 'Cultural integration']
      },
      availability: {
        isAvailable: true,
        timezone: 'America/Los_Angeles'
      }
    }
  ];

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '12',
        ...filters
      });

      const response = await fetch(`${API_BASE_URL}/api/teachers/browse?${params}`);
      if (response.ok) {
        const data = await response.json();
        
        // If no teachers returned from API, use mock data
        if (data.teachers.length === 0 && currentPage === 1) {
          setTeachers(mockTeachers);
          setHasMore(false);
        } else if (data.teachers.length > 0) {
          if (currentPage === 1) {
            setTeachers(data.teachers);
          } else {
            setTeachers(prev => [...prev, ...data.teachers]);
          }
          setHasMore(data.pagination.hasMore);
        } else {
          setHasMore(false);
        }
        setError(null);
      } else {
        // If API fails, use mock data
        if (currentPage === 1) {
          setTeachers(mockTeachers);
          setHasMore(false);
        }
        setError(null);
      }
    } catch (error) {
      console.error('Error fetching teachers:', error);
      // If API call fails, use mock data
      if (currentPage === 1) {
        setTeachers(mockTeachers);
        setHasMore(false);
      }
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const handleSearch = (value: string) => {
    setFilters(prev => ({ ...prev, search: value }));
    setCurrentPage(1);
  };

  const toggleFavorite = (teacherId: string) => {
    const newFavorites = favorites.includes(teacherId)
      ? favorites.filter(id => id !== teacherId)
      : [...favorites, teacherId];
    
    setFavorites(newFavorites);
    localStorage.setItem('teacher-favorites', JSON.stringify(newFavorites));
  };

  const loadMore = () => {
    if (!loading && hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const getRatingStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FaStar
        key={i}
        className={`w-4 h-4 ${
          i < Math.floor(rating)
            ? 'text-yellow-400'
            : i < rating
            ? 'text-yellow-400 opacity-50'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const filteredTeachers = teachers.filter(teacher => {
    if (!filters.search) return true;
    const searchTerm = filters.search.toLowerCase();
    return (
      teacher.basicInfo.fullName.toLowerCase().includes(searchTerm) ||
      teacher.basicInfo.country.toLowerCase().includes(searchTerm) ||
      teacher.profile.subjects.some(subject => subject.toLowerCase().includes(searchTerm)) ||
      teacher.profile.bio.toLowerCase().includes(searchTerm)
    );
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <FaUserGraduate className="text-4xl text-blue-600 mr-3" />
            <h1 className="text-4xl font-bold text-gray-900">
              Our Teachers
            </h1>
          </div>
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            معلمان ما
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Connect with experienced Persian language teachers from around the world. 
            Find the perfect teacher for your learning journey with personalized lessons and cultural insights.
          </p>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col lg:flex-row gap-4 items-center">
            {/* Search Bar */}
            <div className="flex-1 w-full lg:w-auto">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search teachers by name, country, or subjects..."
                  value={filters.search}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FaFilter className="mr-2" />
              Filters
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Country Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <select
                    value={filters.country}
                    onChange={(e) => handleFilterChange('country', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">All Countries</option>
                    <option value="Iran">Iran</option>
                    <option value="USA">USA</option>
                    <option value="Canada">Canada</option>
                    <option value="UK">UK</option>
                    <option value="Germany">Germany</option>
                    <option value="Australia">Australia</option>
                  </select>
                </div>

                {/* Rate Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Min Rate ($/hr)
                  </label>
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minRate}
                    onChange={(e) => handleFilterChange('minRate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Max Rate ($/hr)
                  </label>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxRate}
                    onChange={(e) => handleFilterChange('maxRate', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Sort */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort By
                  </label>
                  <select
                    value={filters.sort}
                    onChange={(e) => handleFilterChange('sort', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="rating">Highest Rated</option>
                    <option value="rate-low">Lowest Price</option>
                    <option value="rate-high">Highest Price</option>
                    <option value="experience">Most Experienced</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Teachers Grid */}
        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading teachers...</p>
            </div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <div className="text-red-500 text-6xl mb-4">⚠️</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {error}
            </h3>
            <button
              onClick={fetchTeachers}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        ) : filteredTeachers.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">👨‍🏫</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No teachers found
            </h3>
            <p className="text-gray-600 mb-6">
              Try adjusting your search criteria or filters.
            </p>
            <button
              onClick={() => {
                setFilters({
                  country: '',
                  minRate: '',
                  maxRate: '',
                  targetStudents: '',
                  sort: 'rating',
                  search: ''
                });
                setCurrentPage(1);
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {filteredTeachers.map((teacher) => (
                <div
                  key={teacher._id}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  {/* Teacher Header */}
                  <div className="relative p-6 border-b border-gray-200">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-xl font-bold mr-4">
                          {teacher.basicInfo.profilePicture ? (
                            <img
                              src={teacher.basicInfo.profilePicture}
                              alt={teacher.basicInfo.fullName}
                              className="w-16 h-16 rounded-full object-cover"
                            />
                          ) : (
                            teacher.basicInfo.fullName.charAt(0)
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            {teacher.basicInfo.fullName}
                          </h3>
                          <div className="flex items-center text-sm text-gray-600">
                            <FaMapMarkerAlt className="mr-1" />
                            {teacher.basicInfo.country}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleFavorite(teacher._id)}
                        className={`p-2 rounded-full transition-colors ${
                          favorites.includes(teacher._id)
                            ? 'text-red-500 bg-red-50'
                            : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                        }`}
                      >
                        <FaHeart className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center mb-3">
                      <div className="flex items-center mr-2">
                        {getRatingStars(teacher.stats.averageRating)}
                      </div>
                      <span className="text-sm text-gray-600">
                        {teacher.stats.averageRating.toFixed(1)} ({teacher.stats.totalReviews} reviews)
                      </span>
                    </div>

                    {/* Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center">
                        <FaClock className="mr-1" />
                        {teacher.stats.totalSessions} sessions
                      </div>
                      <div className="flex items-center">
                        <FaGraduationCap className="mr-1" />
                        {teacher.teachingExperience.yearsTeaching} years
                      </div>
                    </div>
                  </div>

                  {/* Teacher Content */}
                  <div className="p-6">
                    {/* Bio */}
                    <p className="text-gray-700 text-sm mb-4 line-clamp-3">
                      {teacher.profile.bio || "Experienced Persian language teacher with a passion for sharing Iranian culture and language."}
                    </p>

                    {/* Subjects */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Subjects:</h4>
                      <div className="flex flex-wrap gap-2">
                        {teacher.profile.subjects.slice(0, 3).map((subject, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                          >
                            {subject}
                          </span>
                        ))}
                        {teacher.profile.subjects.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                            +{teacher.profile.subjects.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Languages */}
                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Languages:</h4>
                      <div className="flex flex-wrap gap-2">
                        {teacher.profile.languages?.slice(0, 3).map((language, index) => (
                          <span
                            key={index}
                            className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                          >
                            {language}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Rate */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        <FaDollarSign className="text-green-600 mr-1" />
                        <span className="text-lg font-bold text-gray-900">
                          ${teacher.rates.hourlyRate}
                        </span>
                        <span className="text-sm text-gray-600 ml-1">/hour</span>
                      </div>
                      {teacher.rates.offersFreeTrial && (
                        <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                          Free Trial
                        </span>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-3">
                      <Link
                        to={`/teacher/${unmapTeacherId(teacher._id)}`}
                        className="flex-1 flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <FaEye className="mr-2" />
                        View Profile
                      </Link>
                      <Link
                        to={`/teacher-session-payment/${unmapTeacherId(teacher._id)}`}
                        className="flex-1 flex items-center justify-center px-4 py-2 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <FaBook className="mr-2" />
                        Book Session
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Load More */}
            {hasMore && (
              <div className="text-center">
                <button
                  onClick={loadMore}
                  disabled={loading}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Loading...' : 'Load More Teachers'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Teachers; 